import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { CheckCircle2, DollarSign, Users, Zap, Mail, Phone as PhoneIcon, Building } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLeadGuard } from '../components/LeadGuard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    numberType: '',
    countries: '',
    estimatedVolume: '',
    usageType: '',
    message: ''
  });

  const { guard, guardFields, resetGuard } = useLeadGuard();
  const [submitting, setSubmitting] = useState(false);
  const emptyForm = { name: '', email: '', phone: '', company: '', numberType: '', countries: '', estimatedVolume: '', usageType: '', message: '' };

  // A pricing request is a lead: it goes to Admin > Leads and the sales team is emailed.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const details = [
        formData.numberType && `Number type: ${formData.numberType}`,
        formData.countries && `Countries: ${formData.countries}`,
        formData.estimatedVolume && `Estimated monthly volume: ${formData.estimatedVolume}`,
        formData.usageType && `Usage: ${formData.usageType}`,
      ].filter(Boolean).join('\n');
      const response = await fetch(`${BACKEND_URL}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...guardFields(),
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          country: formData.countries ? formData.countries.slice(0, 80) : null,
          service_interest: `Pricing request${formData.numberType ? ` (${formData.numberType})` : ''}`.slice(0, 120),
          message: [details, formData.message].filter(Boolean).join('\n\n').slice(0, 4000) || null,
          source: 'website',
        }),
      });
      resetGuard();
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Could not send your request');
      }
      toast({
        title: "Request Submitted!",
        description: "Our pricing team will contact you within 24 hours with a customized quote.",
      });
      setFormData(emptyForm);
    } catch (err) {
      toast({ title: 'Request not sent', description: `${err.message}. Please try again or email hello@voip2.co.uk.`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="Pricing: Cloud PBX Plans & Phone Numbers"
        description="Transparent VOIP2 pricing for Cloud PBX plans and global phone numbers: compare plans and see what each one includes."
        path="/pricing"
        breadcrumbs={[{ name: 'Pricing', path: '/pricing' }]}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] py-16">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Transparent Pricing
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Get customized pricing tailored to your business needs. No hidden fees, just straightforward solutions.
          </p>
          <div className="flex items-center justify-center space-x-3">
            <DollarSign className="w-6 h-6 text-[#c8ff00]" />
            <span className="text-white font-semibold">Volume discounts available</span>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Flexible Pricing Plans
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Starter Plan */}
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <div className="text-center mb-6">
                <Users className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Starter</h3>
                <p className="text-gray-600">Perfect for small businesses</p>
              </div>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold" style={{ color: '#0a1f2e' }}>Custom</div>
                <div className="text-sm text-gray-600 mt-2">Based on usage</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Up to 10 numbers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Standard support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Basic analytics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Self-service portal</span>
                </li>
              </ul>
            </Card>

            {/* Business Plan */}
            <Card className="p-8 border-2 border-[#c8ff00] hover:shadow-xl transition-shadow relative">
              <div className="absolute top-0 right-0 bg-[#c8ff00] text-[#0a1f2e] px-4 py-1 rounded-bl-lg font-semibold text-sm">
                Popular
              </div>
              <div className="text-center mb-6">
                <Building className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Business</h3>
                <p className="text-gray-600">For growing companies</p>
              </div>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold" style={{ color: '#0a1f2e' }}>Custom</div>
                <div className="text-sm text-gray-600 mt-2">Volume discounts</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Up to 100 numbers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Priority support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Advanced analytics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">API access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Number porting</span>
                </li>
              </ul>
            </Card>

            {/* Enterprise Plan */}
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <div className="text-center mb-6">
                <Zap className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#0a1f2e' }}>Enterprise</h3>
                <p className="text-gray-600">For large organizations</p>
              </div>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold" style={{ color: '#0a1f2e' }}>Custom</div>
                <div className="text-sm text-gray-600 mt-2">Tailored solutions</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Unlimited numbers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Dedicated support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Custom integrations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">SLA guarantees</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Dedicated account manager</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Request Form */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#0a1f2e' }}>
                Request Custom Pricing
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and our team will provide you with a tailored pricing quote based on your specific needs.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    required
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+44 204 575 2222"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="company">Company Name *</Label>
                  <Input
                    id="company"
                    name="company"
                    required
                    placeholder="Your Company Inc."
                    value={formData.company}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="numberType">Number Type *</Label>
                  <select
                    id="numberType"
                    name="numberType"
                    required
                    value={formData.numberType}
                    onChange={handleChange}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c8ff00]"
                  >
                    <option value="">Select type...</option>
                    <option value="local">Local Numbers</option>
                    <option value="national">National Numbers</option>
                    <option value="mobile">Mobile Numbers</option>
                    <option value="2way">2-Way Numbers</option>
                    <option value="tollfree">Toll-Free Numbers</option>
                    <option value="uifn">UIFN Numbers</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="countries">Target Countries *</Label>
                  <Input
                    id="countries"
                    name="countries"
                    required
                    placeholder="e.g., USA, UK, Germany"
                    value={formData.countries}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="estimatedVolume">Estimated Monthly Volume *</Label>
                  <select
                    id="estimatedVolume"
                    name="estimatedVolume"
                    required
                    value={formData.estimatedVolume}
                    onChange={handleChange}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c8ff00]"
                  >
                    <option value="">Select volume...</option>
                    <option value="1-1000">1 - 1,000 minutes</option>
                    <option value="1000-10000">1,000 - 10,000 minutes</option>
                    <option value="10000-50000">10,000 - 50,000 minutes</option>
                    <option value="50000-100000">50,000 - 100,000 minutes</option>
                    <option value="100000+">100,000+ minutes</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="usageType">Primary Usage *</Label>
                  <select
                    id="usageType"
                    name="usageType"
                    required
                    value={formData.usageType}
                    onChange={handleChange}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c8ff00]"
                  >
                    <option value="">Select usage...</option>
                    <option value="voice">Voice Calls</option>
                    <option value="sms">SMS Messaging</option>
                    <option value="both">Voice & SMS</option>
                    <option value="sip">SIP Trunking</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="message">Additional Requirements</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  placeholder="Tell us more about your specific needs, number of DIDs required, any special features, etc."
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-4 justify-end">
                {guard}
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
                  className="hover:opacity-90 font-semibold"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {submitting ? 'Sending…' : 'Request Pricing Quote'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </section>

      {/* Contact Information */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 bg-gray-50">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>
                Need Help Choosing a Plan?
              </h3>
              <p className="text-gray-600 mb-6">
                Our pricing experts are here to help you find the perfect solution for your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center space-x-2">
                  <Mail className="w-5 h-5 text-[#c8ff00]" />
                  <span className="text-gray-700">hello@voip2.co.uk</span>
                </div>
                <div className="flex items-center space-x-2">
                  <PhoneIcon className="w-5 h-5 text-[#c8ff00]" />
                  <span className="text-gray-700">+44 204 575 2222</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
